import { RowSelectionState } from "@tanstack/react-table";
import classNames from "classnames";
import map from "lodash/map";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { stringify } from "PFCore/helpers/use_deterministic_stringify";
import { Profile, ProfileCustomValue } from "PFCore/types";
import { useEffect, useMemo, useRef, useState } from "react";

import { Breadcrumb } from "./breadcrumb";
import { addEntryToChildren, getAllSkillsFromTree, getSkillEntry, Node } from "./helpers";
import { SkillsDrilldown } from "./skills_drilldown";
import css from "./skills_explorer.module.scss";

type SkillsExplorerProps = {
  profile: Profile;
  skills: ProfileCustomValue[];
  className?: string;
  maxSkillNameLength?: number;
  showTooltip?: boolean;
  allowSkillPreview?: boolean;
  canEditSkills?: boolean;
  rowSelectionState?: RowSelectionState;
  setRowSelectionState?: (state: RowSelectionState) => void;
};

type Dimensions = { width: number; height: number };

const calculateChartDimensions = (chartWrapperElement) => ({
  width: chartWrapperElement.offsetWidth,
  height: chartWrapperElement.offsetHeight
});

export const SkillsExplorer = ({
  profile,
  skills,
  showTooltip,
  canEditSkills,
  rowSelectionState,
  setRowSelectionState,
  className,
  maxSkillNameLength,
  allowSkillPreview
}: SkillsExplorerProps) => {
  const [currentPath, setCurrentPath] = useState<number[]>([]);
  const chartWrapperRef = useRef<HTMLDivElement | null>(null);
  const [dimensions, setDimensions] = useState<Dimensions>({ width: 0, height: 0 });

  const debouncedDimensionsChange = useDebounce(setDimensions, 100);

  useEffect(() => {
    if (!chartWrapperRef.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      if (!chartWrapperRef.current) {
        return;
      }
      debouncedDimensionsChange(calculateChartDimensions(chartWrapperRef.current));
    });
    resizeObserver.observe(chartWrapperRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const skillsIds = map(skills, "id");

  useEffect(() => {
    setCurrentPath([]);
  }, [stringify(skillsIds)]);

  // We want to pre-calculate as much of the data we need as possible. Otherwise we run the risk of things
  // like the hover over tooltips performing slowly.
  const skillsTree = useMemo(() => {
    const filteredCategories = (profile?.skillCategories || []).filter((category) =>
      skills?.find((skill) => skill.value === category[0])
    );

    const tree = filteredCategories.reduce((toReturn: Node[], category) => {
      if (category[2] !== "" && category[1] !== "" && category[0] !== "") {
        const rootIndex = addEntryToChildren(toReturn, category[2]);
        const midIndex = addEntryToChildren(toReturn[rootIndex].children as Node[], category[1]);
        const skillIndex = addEntryToChildren(
          toReturn[rootIndex].children?.[midIndex].children as Node[],
          category[0]
        );
        const skill = getSkillEntry(category[0], skills);

        if (skill) {
          // @ts-ignore
          toReturn[rootIndex].children[midIndex].children[skillIndex] = skill;
        }
      }
      return toReturn;
    }, []);

    tree.forEach((node) => {
      if (node.children?.length) {
        node.children.forEach((node) => {
          node.value = getAllSkillsFromTree(node)?.length;
        });
      }
      node.value = getAllSkillsFromTree(node)?.length;
    });

    return tree;
  }, [profile?.skillCategories, skills]);

  const showBreadCrumbs = currentPath.length > 0;

  const height = showBreadCrumbs ? dimensions.height - 40 : dimensions.height;
  const { width } = dimensions;

  return (
    <div className={classNames(css.skillsExplorerWrapper, className)} ref={chartWrapperRef}>
      {showBreadCrumbs && <Breadcrumb skills={skillsTree} path={[...currentPath]} setPath={setCurrentPath} />}
      <SkillsDrilldown
        profile={profile}
        currentPath={currentPath}
        skillsTree={skillsTree}
        setCurrentPath={setCurrentPath}
        height={height}
        width={width}
        showTooltip={showTooltip}
        canEditSkills={canEditSkills}
        rowSelectionState={rowSelectionState}
        setRowSelectionState={setRowSelectionState}
        maxNameLength={maxSkillNameLength}
        allowSkillPreview={allowSkillPreview}
      />
    </div>
  );
};
